import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { UserRole } from '@/lib/types';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const SignupForm: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('patient');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const handleSignup = async () => {
    if (!email || !password || !firstName || !lastName || !role) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all fields.',
        variant: 'destructive',
      });
      return;
    }
  
    try {

      // Fix This Hardcoded setup ** **
      const res = await fetch('https://limitless-possibilities-unleashed.onrender.com/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName, role }),
      });
  
      const result = await res.json();
  
      if (!res.ok) throw new Error(result.error || 'Signup failed');
  
      // ✅ Save user session
      const user = result.user;
      const session = {
        user_id: user.id,
        email: user.email,
        role: user.role,
        auth_level: user.auth_level,
        first_name: user.first_name,
        last_name: user.last_name,
      };
      localStorage.setItem('user_session', JSON.stringify(session));
  
      toast({
        title: 'Signup successful',
        description: 'You are now logged in.',
      });
  
      let route = '/';
      switch (user.role) {
        case 'admin': route = '/admin'; break;
        case 'doctor': route = '/doctor'; break;
        case 'hospital': route = '/hospital'; break;
        case 'patient': route = '/patient'; break;
        case 'staff': route = '/staff'; break;
        case 'insurance': route = '/insurance'; break;
      }
      navigate(route, { replace: true });
  
    } catch (err: any) {
      toast({
        title: 'Signup failed',
        description: err.message || 'Unexpected error',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <form onSubmit={handleSignup} className="space-y-4 w-full max-w-md mx-auto">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <Input
          id="phoneNumber"
          type="tel"
          placeholder="+1 (123) 456-7890"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
        <p className="text-xs text-gray-500">Password must be at least 6 characters</p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
          <SelectTrigger>
            <SelectValue placeholder="Select your role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="patient">Patient</SelectItem>
            <SelectItem value="doctor">Doctor</SelectItem>
            <SelectItem value="hospital">Hospital Admin</SelectItem>
            <SelectItem value="insurance">Insurance Provider</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {errorMessage && (
        <div className="text-sm text-red-500 mt-2">
          {errorMessage}
        </div>
      )}
      
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <span className="flex items-center justify-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : 'Sign up'}
      </Button>
      
      <div className="text-sm text-center text-gray-500 mt-4">
        <p>Already have an account? <a href="/auth" className="text-blue-600 hover:underline" onClick={(e) => {
          e.preventDefault();
          document.querySelector('[value="login"]')?.dispatchEvent(new Event('click', { bubbles: true }));
        }}>Log in</a></p>
      </div>
    </form>
  );
};
